import { createElement, useState } from "react";
import { useTranslation } from "../../../../localization/useTranslation";
import { LegalEntityAuditRowModel, LegalEntityAuditStatus } from "../../../../legalEntity/legalEntityAuditTab/LegalEntityAudit.types";
import { getQuestionnaireResponseScore } from "../../../questionnaireResponseScore";
import { useDownloadLegalEntityAuditReport } from "../../../../legalEntityAuditGroup/DowloadLegalEntityAuditReport.hooks";
import { IExternalAuditTableProps } from "./ExternalAuditTable";
import { htmlToPlainText, isNullOrWhitespace } from "@common/stringOperations.ts";
import { EntityType } from "@common/Common.types.ts";
import { Trans } from "react-i18next";
import { downloadFile } from "@common/api/downloadFile.ts";
import { getDownloadDocumentVersionUrl } from "@common/components/dotLegalFileUploadTable/DotLegalFileUploadTable.hooks.ts";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery.ts";
import { getLegalEntityQueryKey } from "../../../../legalEntity/LegalEntity.hooks.ts";
import { useRefetchWhenPaneSaves } from "../../../../main/panes/usePanes";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { setFirstLetterToLowerCase } from "@common/stringOperations.ts";
import { RiskLevel } from "../../../../legalEntity/legalEntityClassificationTab/LegalEntityClassificationTab.types.ts";
import { EvaluationStatus } from "../evaluateQuestionnaireDialog/EvaluateQuestionnaireDialog.types.ts";
import { getUniqueFilterOptions, getScoreFilterFixedData, matchesScoreFilter } from "@common/hooks/filterUtils.ts";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { useRowNoteDialog } from "@common/components/dotlegalNote/useRowNoteDialog";

export default function useExternalAuditTableHooks(props: IExternalAuditTableProps) {
    const { translateString, translateLegalEntityAuditStatus, translateEvaluationEnum } = useTranslation();
    const { getReportPdfUrl, isDownloadingPdf, onDownloadReportAsPdf } = useDownloadLegalEntityAuditReport();
    const { refetchQueriesByPredicate } = useDotLegalQueryClient();
    const snackbar = useDotLegalSnackbar();
    useRefetchWhenPaneSaves(props.refetch);

    const [showAddToAuditGroup, setShowAddToAuditGroup] = useState<string | undefined>(undefined);
    const [downloadingDocument, setDownloadingDocument] = useState("");
    const {
        selectFilters: filters,
        filterOperators,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useFilterState(props.sessionStorageKey ?? "external-audit-filters");

    const onAuditClosed = () => {
        props.refetch();
    };

    const onAuditReopened = () => {
        props.refetch();
    };

    const onAuditDeleted = () => {
        if (props.legalEntityId) {
            refetchQueriesByPredicate((query) =>
                query.queryKey.some((key) => typeof key === "string" && key.includes(getLegalEntityQueryKey(props.legalEntityId!)))
            );
        }
        props.refetch();
    };

    const onResponsibleChanged = () => {
        props.refetch();
    };

    const onContactChanged = () => {
        props.refetch();
    };

    const onReminderSent = () => {
        props.refetch();
    };

    const onAuditAddedToAuditGroup = () => {
        props.refetch();
        setShowAddToAuditGroup(undefined);
    };

    const onAuditEvaluated = () => {
        props.refetch();
    };

    const onRemindersToggled = () => {
        props.refetch();
    };

    const auditRowModels: undefined | Array<LegalEntityAuditRowModel> = props.data?.map((a) => {
        return {
            ...a,
            relatedEntitiesString:
                a.assets.length > 0
                    ? a.assets.map((asset) => asset.name).join(", ")
                    : a.processingActivities.map((processingActivity) => processingActivity.name).join(", "),
            contactName: a.contact?.name ?? "",
            additionalRecipientNames: a.additionalRecipients.map((additionalRecipient) => additionalRecipient.name).join(", "),
            responsibleName: a.responsible.name,
            auditGroupName: a.auditGroup?.name,
            statusName: translateLegalEntityAuditStatus(a.status.toString()),
            riskLevelName: a.scoreEnabled ? a.riskName : translateString("none"),
            scoreName: getQuestionnaireResponseScore(
                a.status === LegalEntityAuditStatus.Submitted,
                a.scoreEnabled,
                a.score,
                translateString("scoreNotEnabled")
            ),
            evaluationName: a.evaluationStatus ? translateEvaluationEnum(a.evaluationStatus.toString()) : "",
            legalEntityName: a.legalEntity?.name,
            notePlainText: htmlToPlainText(a.note),
        };
    });

    const { getNoteMenuItem, noteDialogProps } = useRowNoteDialog<LegalEntityAuditRowModel>({
        getPostUrl: (row) => `/legalEntityAudit/${row.id}/note`,
        entityType: EntityType.LegalEntityAudit,
        onSaved: props.refetch,
    });

    const filteredAudits = applyClientFilters(
        auditRowModels,
        [
            { id: "template", candidate: (r) => r.questionnaireId },
            { id: "status", candidate: (r) => r.status.toString() },
            {
                id: "riskLevel",
                candidate: (r) => (r.riskLevel !== undefined && r.riskLevel !== null ? r.riskLevel.toString() : RiskLevel.None.toString()),
            },
            { id: "responsible", candidate: (r) => r.responsible.id },
            { id: "sender", candidate: (r) => r.sender },
            { id: "auditGroup", candidate: (r) => r.auditGroup?.id },
            { id: "evaluation", candidate: (r) => r.evaluationStatus?.toString() },
            { id: "legalEntity", candidate: (r) => r.legalEntity?.id },
            { id: "sentYear", candidate: (r) => (r.sent ? new Date(r.sent).getFullYear().toString() : undefined) },
        ],
        filters,
        {},
        filterOperators,
        (row) => matchesScoreFilter(filters.score ?? [], filterOperators.score, row)
    );

    function getSelectableYearsSent() {
        if (!auditRowModels) return [];
        const years = auditRowModels.filter((x) => x.sent).map((x) => new Date(x.sent!).getFullYear().toString());
        const uniqueYears = [...new Set(years)].sort((a, b) => b.localeCompare(a));
        return uniqueYears.map((year) => ({ id: year, name: year }));
    }

    function getFilterOptions(): Array<FilterOption> {
        const options: Array<FilterOption> = [
            {
                id: "template",
                label: translateString("template"),
                type: FilterOptionType.SelectField,
                value: filters.template ?? [],
                fixedData: getUniqueFilterOptions(
                    auditRowModels,
                    (r) => r.questionnaireName,
                    (r) => r.questionnaireId
                ),
            },
            {
                id: "sentYear",
                label: translateString("legalEntityAuditSent"),
                type: FilterOptionType.SelectField,
                value: filters.sentYear ?? [],
                fixedData: getSelectableYearsSent(),
            },
            {
                id: "responsible",
                label: translateString("responsible"),
                type: FilterOptionType.SelectField,
                value: filters.responsible ?? [],
                fixedData: getUniqueFilterOptions(
                    auditRowModels,
                    (r) => r.responsibleName,
                    (r) => r.responsible.id
                ),
            },
            {
                id: "sender",
                label: translateString("sentFrom"),
                type: FilterOptionType.SelectField,
                value: filters.sender ?? [],
                fixedData: getUniqueFilterOptions(auditRowModels, (r) => r.sender),
            },
            {
                id: "auditGroup",
                label: translateString("auditGroup"),
                type: FilterOptionType.SelectField,
                value: filters.auditGroup ?? [],
                fixedData: getUniqueFilterOptions(
                    auditRowModels,
                    (r) => r.auditGroupName,
                    (r) => r.auditGroup?.id
                ),
            },
            {
                id: "status",
                label: translateString("status"),
                type: FilterOptionType.SelectField,
                value: filters.status ?? [],
                fixedData: Object.values(LegalEntityAuditStatus)
                    .filter((v): v is number => typeof v === "number")
                    .map((v) => ({ id: v.toString(), name: translateLegalEntityAuditStatus(v.toString()) })),
            },
            {
                id: "riskLevel",
                label: translateString("riskLevel"),
                type: FilterOptionType.SelectField,
                value: filters.riskLevel ?? [],
                fixedData: Object.values(RiskLevel)
                    .filter((v): v is number => typeof v === "number")
                    .map((v) => ({
                        id: v.toString(),
                        name: v === RiskLevel.None ? translateString("none") : translateString(setFirstLetterToLowerCase(RiskLevel[v])),
                    })),
            },
            {
                id: "score",
                label: translateString("score"),
                type: FilterOptionType.SelectField,
                value: filters.score ?? [],
                fixedData: getScoreFilterFixedData(translateString),
            },
            {
                id: "evaluation",
                label: translateString("evaluation"),
                type: FilterOptionType.SelectField,
                value: filters.evaluation ?? [],
                fixedData: Object.values(EvaluationStatus)
                    .filter((v): v is number => typeof v === "number")
                    .map((v) => ({ id: v.toString(), name: translateEvaluationEnum(v.toString()) })),
            },
        ];

        if (props.pageType === "system" || props.pageType === "processingActivity") {
            options.splice(2, 0, {
                id: "legalEntity",
                label: translateString("legalEntity"),
                type: FilterOptionType.SelectField,
                value: filters.legalEntity ?? [],
                fixedData: getUniqueFilterOptions(
                    auditRowModels,
                    (r) => r.legalEntityName,
                    (r) => r.legalEntity?.id
                ),
            });
        }

        return withOperators(options, filterOperators);
    }

    const hasActiveFilters = Object.values(filters).some((v) => v.length > 0);

    const onDownloadClick = async (documentId: string, dataId: string, fileName: string, link: string | undefined, entityId: string) => {
        if (!isNullOrWhitespace(fileName)) {
            setDownloadingDocument(documentId);
            await downloadDocumentVersion(documentId, dataId, fileName, EntityType.LegalEntity, entityId);
            setDownloadingDocument("");
        } else {
            window.open(link, "_blank");
        }
    };

    async function downloadDocumentVersion(documentId: string, dataId: string, fileName: string, entityType: EntityType, entityId: string) {
        let url = getDownloadDocumentVersionUrl(documentId, dataId, entityType, entityId);

        snackbar.show(createElement(Trans, { i18nKey: "documentIsBeingDownloaded", values: { document: fileName } }));
        await downloadFile(url, fileName);
    }

    return {
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        audits: filteredAudits,
        hasAudits: props.isLoading || (props.data && props.data.length > 0),
        showAddToAuditGroup,
        setShowAddToAuditGroup,
        onAuditAddedToAuditGroup,
        onAuditClosed,
        onAuditReopened,
        onAuditDeleted,
        onDownloadReportAsPdf,
        isDownloadingPdf,
        getReportPdfUrl,
        onResponsibleChanged,
        onContactChanged,
        onReminderSent,
        onAuditEvaluated,
        onRemindersToggled,
        downloadingDocument,
        onDownloadClick,
        getFilterOptions,
        onFilterChange,
        hasActiveFilters,
        getNoteMenuItem,
        noteDialogProps,
    };
}
